import type { AppProps, NextWebVitalsMetric } from 'next/app'

import '@grammarly/badger-ui/dist/css/index.css'
import '@grammarly/website-components/dist/css/index.css'
import '@grammarly/inky-ui/dist/css/index.css'
import '@grammarly/assistant-ui-kit/style.css'

import { feMetrics } from '@lib/tracking/metrics/femetrics'
import * as CmsMetrics from '@lib/tracking/metrics/metrics'
import { StatsigGates } from '@lib/services/experiments/gates'
import { sanitizeExperimentGroupName } from '@lib/services/experiments/util'

import ErrorBoundary from '../components/error-boundary'
import { BRANCH_NAME } from '../constants'

import '../components/page/global.scss'

const getPageSlug = (): string => {
  return window.location.pathname === '/' ? window.location.pathname : window.location.pathname.slice(1)
}

const shouldLogWebVitals = () => {
  const treatments = window?.Grammarly?.treatments || []

  return treatments.some(({ experimentName }) => experimentName === StatsigGates.WebVitalsTracking.name)
}

const getExperimentInfo = () => {
  const serverSideExperiments = window?.Grammarly?.serverSideAssignTreatments ?? []

  if (serverSideExperiments.length === 0) {
    // Fake experiment info to be able to compare perf when using holdouts
    return {
      experimentName: 'null',
      experimentVariant: 'null',
    }
  }

  // We take the first experiment from the server side - this may be non-deterministic if there are multiple experiments
  const serverSideAssignExperiment = serverSideExperiments.find((treatment) => treatment.type === 'experiment')

  if (serverSideAssignExperiment) {
    return {
      experimentName: serverSideAssignExperiment.experimentName,
      experimentVariant: sanitizeExperimentGroupName(serverSideAssignExperiment.groupName),
    }
  }

  // Fake experiment info to be able to compare perf when using holdouts
  return {
    experimentName: 'null',
    experimentVariant: 'null',
  }
}

const reportableMetrics = new Set<string>([
  CmsMetrics.WebVitals.Hydration,
  CmsMetrics.WebVitals.TTFB,
  CmsMetrics.WebVitals.FCP,
  CmsMetrics.WebVitals.LCP,
  CmsMetrics.WebVitals.FID,
  CmsMetrics.WebVitals.CLS,
  CmsMetrics.WebVitals.INP,
])

export function reportWebVitals(metric: NextWebVitalsMetric) {
  const metricName: string = metric.name === 'Next.js-hydration' ? CmsMetrics.WebVitals.Hydration : metric.name
  const metricValue: number = Number.parseFloat(metric.value.toFixed(2))

  if (reportableMetrics.has(metricName) && shouldLogWebVitals()) {
    feMetrics
      .getMetric(CmsMetrics.GeneralClientMetrics.WebVitals)
      .getTimer(metricName)
      .setContext({
        pageSlug: getPageSlug(),
        branchName: BRANCH_NAME,
        ...getExperimentInfo(),
      })
      .recordTime(metricValue)
  }
}

export default function CmsApp({ Component, pageProps }: AppProps) {
  return (
    <ErrorBoundary>
      <Component {...pageProps} />
    </ErrorBoundary>
  )
}
